import { Link, useNavigate } from "react-router-dom";
import { Sparkles, ArrowLeft, Heart, Zap, Star, Flame, CircleDollarSign, Crosshair, Package, Award, X, Eye } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect, useCallback } from "react";
import { useProductStore } from "../stores/useProductStore";
import { getProductImages } from "../lib/imageUtils";

const FeaturedProducts = ({ onAddToWishlist, onAddToCart, gameMode }) => {
	const navigate = useNavigate();
	
	// שימוש ב-store למוצרים מומלצים
	const { featuredProducts, loading, error, fetchFeaturedProducts } = useProductStore();
	const [quickActions, setQuickActions] = useState({});
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [selectedProduct, setSelectedProduct] = useState(null);

	// טעינת מוצרים מומלצים
	useEffect(() => {
		fetchFeaturedProducts();
	}, [fetchFeaturedProducts]);

	const handleImageClick = (product) => {
		setSelectedProduct(product);
		setIsModalOpen(true);
	};

	const handleViewProduct = useCallback((product) => {
		if (!product || !product._id) {
			console.error('❌ FeaturedProducts: No product ID available for navigation');
			return;
		}
		console.log('🚀 FeaturedProducts: Navigating to product', product._id);
		navigate(`/product/${product._id}`);
	}, [navigate]);

	const handleModalClose = () => {
		setIsModalOpen(false);
		setSelectedProduct(null);
	};

	// סגירה ב-Escape או בלחיצה מחוץ למודל
	useEffect(() => {
		if (!isModalOpen) return;
		const handleKeyDown = (e) => {
			if (e.key === "Escape") handleModalClose();
		};
		const handleClickOutside = (e) => {
			if (e.target.classList.contains('modal-overlay')) {
				handleModalClose();
			}
		};
		document.addEventListener("keydown", handleKeyDown);
		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("keydown", handleKeyDown);
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [isModalOpen]);

	// בחירת מוצרים מומלצים או מוצרים עם תג "מומלץ"
	const recommendedProducts = featuredProducts && Array.isArray(featuredProducts)
		? featuredProducts
			.filter(product => product.isFeatured)
			.slice(0, 8)
		: [];

	// אם אין מוצרים מומלצים, נציג את המוצרים הראשונים
	const productsToShow = recommendedProducts.length > 0 
		? recommendedProducts 
		: (featuredProducts && Array.isArray(featuredProducts) ? featuredProducts.slice(0, 8) : []);

	const getFeaturedProductImage = (product) => getProductImages(product)[0];

	// הצגת loading אם הנתונים נטענים
	if (loading) {
		return (
			<section className="relative mt-20 py-16 overflow-hidden" dir="rtl">
				<div className="container mx-auto px-4">
					<div className="text-center">
						<div className="animate-spin rounded-full h-16 w-16 border-b-2 border-brand-primary mx-auto"></div>
						<p className="text-white mt-4">טוען מוצרים מומלצים...</p>
					</div>
				</div>
			</section>
		);
	}

	// הצגת error אם יש שגיאה
	if (error) {
		return (
			<section className="relative mt-20 py-16 overflow-hidden" dir="rtl">
				<div className="container mx-auto px-4">
					<div className="text-center">
						<p className="text-red-500 mb-4">שגיאה בטעינת המוצרים המומלצים</p>
						<button 
							onClick={fetchFeaturedProducts}
							className="bg-brand-primary text-white px-4 py-2 rounded-lg hover:bg-opacity-80"
						>
							נסה שוב
						</button>
					</div>
				</div>
			</section>
		);
	}

	// הוספת אלמנטים של משחק
	const getGameModeBonus = (index) => {
		if (!gameMode) return null;
		
		const bonuses = [
			{ type: "points", value: 50, icon: CircleDollarSign, text: "50 נקודות" },
			{ type: "discount", value: 15, icon: Crosshair, text: "15% הנחה" },
			{ type: "gift", value: 0, icon: Package, text: "מתנה חינם" },
			{ type: "vip", value: 0, icon: Award, text: "VIP Access" }
		];
		
		return bonuses[index % bonuses.length];
	};

	// אנימציית הוספה לעגלה
	const handleAddToCart = (product) => {
		if (onAddToCart) {
			onAddToCart(product);
		}
		
		// אנימציית זמנית
		setQuickActions(prev => ({
			...prev,
			[product._id]: 'cart'
		}));
		
		setTimeout(() => {
			setQuickActions(prev => ({
				...prev,
				[product._id]: null
			}));
		}, 2000);
	};

	// אנימציית הוספה לרשימת הרצונות
	const handleAddToWishlist = (product) => {
		if (onAddToWishlist) {
			onAddToWishlist(product);
		}
		
		// אנימציית זמנית
		setQuickActions(prev => ({
			...prev,
			[product._id]: 'wishlist'
		}));
		
		setTimeout(() => {
			setQuickActions(prev => ({
				...prev,
				[product._id]: null
			}));
		}, 2000);
	};

	return (
		<>
			<section className="relative mt-20 py-16 overflow-hidden" dir="rtl">
			{/* Background Effects */}
			<div className="absolute inset-0 bg-gradient-to-b from-transparent to-transparent" style={{background: 'linear-gradient(to bottom, transparent, rgba(100, 13, 70, 0.1), transparent)'}}></div>
			<div className="absolute top-0 left-1/4 w-72 h-72 rounded-full blur-3xl animate-pulse" style={{backgroundColor: 'rgba(100, 13, 70, 0.1)'}}></div>
			<div className="absolute bottom-0 right-1/4 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
			
			<div className="relative z-10 container mx-auto px-4">
				{/* Header Section */}
				<div className="text-center mb-12">
					<div className="inline-flex items-center gap-2 px-6 py-2 rounded-full mb-4" style={{background: 'linear-gradient(to right, rgba(100, 13, 70, 0.2), rgba(139, 69, 19, 0.2))'}}>
						<Sparkles className="w-5 h-5 text-brand-primary" />
						<span className="font-medium text-sm" style={{color: 'rgba(100, 13, 70, 0.8)'}}>קולקציה מובחרת</span>
					</div>
					
					<h2 className="text-4xl md:text-5xl font-bold mb-6">
						<span className="text-brand-primary">
							{gameMode ? "🎮 מוצרים מומלצים - מצב משחק" : "מוצרים מומלצים"}
						</span>
					</h2>
					
					<p className="text-gray-300 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed">
						{gameMode 
							? "זכה בנקודות ובונוסים עם כל רכישה! צבור נקודות והפוך לחבר VIP"
							: "גלה את הקולקציה המובחרת שלנו של תכשיטים ייחודיים בעבודת יד"
						}
					</p>
				</div>
				
				{/* Products Grid */}
				<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 mb-12">
					{productsToShow.map((product, index) => {
						const gameBonus = getGameModeBonus(index);
						const productImage = getFeaturedProductImage(product);
						return (
							<motion.div 
								key={product._id}
								initial={{ opacity: 0, y: 20 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ delay: index * 0.1 }}
								className="group relative"
								whileHover={{ 
									y: -4,
									scale: 1.01,
									rotateY: 1,
									rotateX: -0.5
								}}
								style={{
									perspective: '1000px',
									transformStyle: 'preserve-3d'
								}}
							>
								{/* Product Card Container - Apple Style 3D Widget */}
								<div className="apple-widget-3d overflow-hidden float-3d-animation relative">
									{/* Game Mode Bonus Badge - Apple Style */}
									{gameMode && gameBonus && (
										<motion.div
											initial={{ scale: 0, rotate: -12 }}
											animate={{ scale: 1, rotate: 0 }}
											className="absolute top-3 right-3 z-20 bg-white/20 backdrop-blur-xl text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg border border-white/30"
										>
											<div className="flex items-center gap-1">
												<gameBonus.icon className="w-3 h-3" />
												<span>{gameBonus.text}</span>
											</div>
										</motion.div>
									)}
									{/* Product Image - Apple Style with Popup */}
                  <div className="aspect-square bg-gradient-to-br from-blue-400/20 to-purple-500/20 relative overflow-hidden rounded-t-3xl">
                    {product && productImage ? (
											<img
                        src={productImage}
												alt={product && product.name ? product.name : 'מוצר'}
												className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 cursor-pointer"
												onClick={() => handleImageClick(product)}
											/>
										) : (
											<div className="w-full h-full flex items-center justify-center">
												<Sparkles className="w-16 h-16 text-white/60" />
											</div>
										)}
										

										
										{/* Hot Badge - Apple Style */}
										{index < 3 && (
											<div className="absolute top-3 left-3 bg-white/20 backdrop-blur-xl text-white px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1 border border-white/30">
												<Flame className="w-3 h-3" />
												<span>חם</span>
											</div>
										)}
										
										{/* Quick Actions - Apple Style
										<div className="absolute inset-0 bg-black/20 backdrop-blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-center justify-center gap-4">
											<motion.button
												whileHover={{ scale: 1.1 }}
												whileTap={{ scale: 0.9 }}
												onClick={() => product && handleAddToWishlist(product, index)}
												className="bg-white/20 backdrop-blur-xl p-3 rounded-full text-white hover:bg-white/30 transition-colors border border-white/30"
											>
												<Heart className="w-5 h-5" />
											</motion.button>
											<motion.button
												whileHover={{ scale: 1.1 }}
												whileTap={{ scale: 0.9 }}
												onClick={() => product && handleAddToCart(product, index)}
												className="bg-white/20 backdrop-blur-xl p-3 rounded-full text-white hover:bg-white/30 transition-colors border border-white/30"
											>
												<ShoppingCart className="w-5 h-5" />
											</motion.button>
										</div> */}
									</div>
									
									{/* Product Info - Apple Style 3D Widget */}
									<div className="p-6 glass-3d">
										<div className="flex items-center justify-between mb-3">
											<h3 className="font-bold text-white text-lg">{product && product.name ? product.name : 'מוצר'}</h3>
											{product && product.isFeatured && (
												<div className="flex items-center gap-1 bg-white/20 backdrop-blur-xl px-2 py-1 rounded-full border border-white/30">
													<Star className="w-3 h-3 text-yellow-300" />
													<span className="text-yellow-300 text-xs">מומלץ</span>
												</div>
											)}
										</div>
										
										<div className="mb-4">
											<p className="text-white/70 text-sm leading-relaxed line-clamp-2">
												{product && product.description ? product.description : 'תיאור לא זמין'}
											</p>
											{product && product.description && product.description.length > 100 && (
												<button 
													onClick={() => handleImageClick(product)}
													className="text-brand-primary text-xs mt-2 hover:text-brand-primary/80 transition-colors flex items-center gap-1"
												>
													<span>קרא עוד</span>
													<svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
														<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
													</svg>
												</button>
											)}
										</div>
										
										<div className="flex items-center justify-between mb-4">
											<div className="text-2xl font-bold text-white">
												₪{product && product.price ? product.price : '0.00'}
											</div>
											{gameMode && (
												<div className="flex items-center gap-1 text-yellow-300 bg-white/10 backdrop-blur-xl px-2 py-1 rounded-full border border-white/20">
													<Zap className="w-3 h-3" />
													<span className="text-xs">+{10 + index * 5} נקודות</span>
												</div>
											)}
										</div>
										
										{/* Action Buttons - Apple Style 3D */}
										<div className="flex gap-2">
											<motion.button
												whileHover={{ 
													scale: 1.02, 
													y: -1,
													rotateX: -0.5,
													rotateY: 0.5
												}}
												whileTap={{ scale: 0.98 }}
												onClick={() => product && handleAddToCart(product, index)}
												className="flex-1 bg-white/20 backdrop-blur-xl text-white py-3 rounded-2xl font-bold hover:bg-white/30 transition-all duration-800 border border-white/30 shadow-lg hover:shadow-xl"
												style={{ transformStyle: 'preserve-3d' }}
											>
												הוסף לעגלה
											</motion.button>
											<motion.button
												whileHover={{ 
													scale: 1.02, 
													y: -1,
													rotateX: -0.5,
													rotateY: 0.5
												}}
												whileTap={{ scale: 0.98 }}
												onClick={() => product && handleViewProduct(product)}
												className="bg-blue-500/20 backdrop-blur-xl text-white p-3 rounded-2xl hover:bg-blue-500/30 transition-all duration-800 border border-blue-500/30 shadow-lg hover:shadow-xl"
												style={{ transformStyle: 'preserve-3d' }}
											>
												<Eye className="w-5 h-5" />
											</motion.button>
											<motion.button
												whileHover={{ 
													scale: 1.02, 
													y: -1,
													rotateX: -0.5,
													rotateY: 0.5
												}}
												whileTap={{ scale: 0.98 }}
												onClick={() => product && handleAddToWishlist(product, index)}
												className="bg-white/20 backdrop-blur-xl text-white p-3 rounded-2xl hover:bg-white/30 transition-all duration-800 border border-white/30 shadow-lg hover:shadow-xl"
												style={{ transformStyle: 'preserve-3d' }}
											>
												<Heart className="w-5 h-5" />
											</motion.button>
										</div>
									</div>
								</div>
								
								{/* Action Feedback */}
								<AnimatePresence>
									{product && quickActions[product._id] === 'cart' && (
										<motion.div
											initial={{ opacity: 0, y: 10 }}
											animate={{ opacity: 1, y: 0 }}
											exit={{ opacity: 0, y: -10 }}
											className="absolute bottom-0 left-0 right-0 bg-green-500/90 backdrop-blur-sm text-white p-2 rounded-b-2xl text-center text-sm font-bold"
										>
											✅ נוסף לעגלה!
										</motion.div>
									)}
									{product && quickActions[product._id] === 'wishlist' && (
										<motion.div
											initial={{ opacity: 0, y: 10 }}
											animate={{ opacity: 1, y: 0 }}
											exit={{ opacity: 0, y: -10 }}
											className="absolute bottom-0 left-0 right-0 bg-red-500/90 backdrop-blur-sm text-white p-2 rounded-b-2xl text-center text-sm font-bold"
										>
											❤️ נוסף לרשימת הרצונות!
										</motion.div>
									)}
								</AnimatePresence>
							</motion.div>
						);
					})}
				</div>
				
				{/* Enhanced Call to Action */}
				<div className="text-center">
					{/* Stats */}
					<div className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-8">
						<motion.div 
							initial={{ opacity: 0, x: -20 }}
							animate={{ opacity: 1, x: 0 }}
							className="flex items-center gap-2 text-gray-400"
						>
							<div className="w-2 h-2 bg-brand-primary rounded-full animate-pulse"></div>
							<span className="text-sm">מעל 1000 לקוחות מרוצים</span>
						</motion.div>
						<motion.div 
							initial={{ opacity: 0, x: 0 }}
							animate={{ opacity: 1, x: 0 }}
							transition={{ delay: 0.1 }}
							className="flex items-center gap-2 text-gray-400"
						>
							<div className="w-2 h-2 bg-brand-primary rounded-full animate-pulse delay-300"></div>
							<span className="text-sm">משלוח חינם מעל 299₪</span>
						</motion.div>
						<motion.div 
							initial={{ opacity: 0, x: 20 }}
							animate={{ opacity: 1, x: 0 }}
							transition={{ delay: 0.2 }}
							className="flex items-center gap-2 text-gray-400"
						>
							<div className="w-2 h-2 bg-brand-primary rounded-full animate-pulse delay-600"></div>
							<span className="text-sm">החזרות עד 14 יום</span>
						</motion.div>
					</div>
					
					{/* Action Buttons */}
					<div className="flex flex-col sm:flex-row items-center justify-center gap-4">
						<Link
							to="/category/necklaces"
							className="group inline-flex items-center gap-3 px-8 py-4 bg-brand-primary bg-brand-primary-hover text-white font-bold rounded-2xl transition-all duration-300 hover:shadow-2xl hover:scale-105 transform"
							style={{'--tw-shadow-color': 'rgba(100, 13, 70, 0.25)'}}
						>
							<span>צפה בכל המוצרים</span>
							<ArrowLeft className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
						</Link>
						
						{gameMode && (
							<motion.div
								initial={{ opacity: 0, scale: 0.8 }}
								animate={{ opacity: 1, scale: 1 }}
								className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-2xl font-bold"
							>
								<CircleDollarSign className="w-5 h-5" />
								<span>מצב משחק פעיל!</span>
							</motion.div>
						)}
					</div>
				</div>
			</div>
		</section>

		{/* מודל תמונה מוגדלת */}
		<AnimatePresence>
			{isModalOpen && selectedProduct && (
				<motion.div
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					exit={{ opacity: 0 }}
					className="fixed inset-0 bg-black/95 backdrop-blur-xl z-50 flex items-center justify-center p-4 modal-overlay"
					onClick={handleModalClose}
				>
					<motion.div
						initial={{ scale: 0.8, opacity: 0 }}
						animate={{ scale: 1, opacity: 1 }}
						exit={{ scale: 0.8, opacity: 0 }}
						className="relative max-w-4xl max-h-[90vh] bg-white/10 backdrop-blur-xl rounded-3xl border border-white/20 shadow-2xl overflow-hidden"
						onClick={(e) => e.stopPropagation()}
					>
						{/* Close Button */}
						<button
							onClick={handleModalClose}
							className="absolute top-4 right-4 bg-white/20 backdrop-blur-xl text-white p-3 rounded-full hover:bg-white/30 transition-all duration-300 border border-white/30 z-10"
						>
							<X className="w-5 h-5" />
						</button>
						
						{/* Image */}
                        <img
                          src={getFeaturedProductImage(selectedProduct)}
							alt={selectedProduct.name}
							className="w-full h-full object-contain max-h-[80vh]"
						/>
						
						{/* Product Info */}
						<div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
							<h3 className="text-white text-xl font-bold mb-2">{selectedProduct.name}</h3>
							<p className="text-white/80 text-sm">{selectedProduct.description}</p>
							<div className="text-white text-lg font-bold mt-2">₪{selectedProduct.price}</div>
						</div>
					</motion.div>
				</motion.div>
			)}
		</AnimatePresence>
	</>
	);
};

export default FeaturedProducts;
